import type { ConversationMessage } from "@/stores/conversation-store";
import type { ContextualStoryState } from "./contextual-story-state";
import type { ActiveExecution } from "@/stores/companion-store";

/** Accepted turns in this exact chat own the current observation and question. */
export function contextualConversationExecution(
  story: ContextualStoryState | null,
  messages: ConversationMessage[],
  executions: Iterable<ActiveExecution> = [],
): string | null {
  if (!story) return null;
  const original = messages.find(
    (message) =>
      message.role === "user" &&
      !message.isOptimistic &&
      message.clientRequestId === story.requestId,
  );
  const fallback = story.executionId ?? original?.executionId ?? null;
  const conversationId = story.conversationId ?? original?.conversationId;
  if (!conversationId || !["looking", "live"].includes(story.phase))
    return fallback;
  const baseline = original ? Date.parse(original.createdAt) : story.startedAt;
  let latestTime = baseline;
  let executionId = fallback;
  for (const message of messages) {
    if (
      message.id === original?.id ||
      !["user", "assistant"].includes(message.role) ||
      message.isOptimistic ||
      message.nativeSubagentId ||
      message.backgroundResult ||
      message.directResult ||
      message.activityKind ||
      message.conversationId !== conversationId ||
      !message.executionId
    )
      continue;
    const timestamp = Date.parse(
      message.executionStartedAt ?? message.createdAt,
    );
    if (timestamp >= latestTime) {
      latestTime = timestamp;
      executionId = message.executionId;
    }
  }
  // Automatic concierge turns have no live user-message broadcast. Their
  // ordinary execution receipt identifies the active root before its reply.
  for (const execution of executions) {
    if (
      execution.conversationId !== conversationId ||
      execution.spaceId !== story.projectId ||
      execution.nativeSubagent ||
      execution.origin === "heartbeat" ||
      !execution.startedAt
    )
      continue;
    const timestamp = Date.parse(execution.startedAt);
    if (timestamp >= latestTime) {
      latestTime = timestamp;
      executionId = execution.id;
    }
  }
  return executionId;
}
